import { EventEmitter } from "events";

import type { ReactNode } from "react";

import dayjs from "dayjs";
import {
    noop,
    merge,
    result,
    partition,
    isString,
    isBoolean,
    isEmpty,
    find,
    identity,
    pick,
    keys,
    isEqual,
    camelCase,
    includes,
    head,
    trimEnd,
    cloneDeep,
    each,
    get,
    isFunction,
    reduce,
} from "lodash";

import Sentry from "@similarweb/sentry-logger";

import categoryService from "app/common/services/categoryService";
import type { ILocationObject } from "app/components/campaigns/queryBar-report/reports-pages-filters/CmapaignsIfraTypes";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import { getCustomIndustryIdFromModel } from "app/components/dashboard/widget-wizard/utils/dashboardWizardUtils";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import * as allDataFetchers from "app/components/widget/widget-fetchers";
import { WidgetExporter } from "app/exporters/WidgetExporter";
import { i18nFilter } from "app/filters/i18nFilter";
import type { EDevice } from "app/pages/app-intelligence/common-components/device-picker/types";
import type { CampaignConfigForDashboard } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import type { UntaggedFilterValue } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import CountryService from "app/services/CountryService";
import type { IDurationData } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import type { IEmitterEmit, IEmitterListener } from "app/services/EmitterTypes";
import { keywordGroupsKeys } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import type { IPptSlideExportRequest } from "app/services/PptExportService/PptExportServiceTypes";
import { fetchWebsiteImageQuery } from "app/services/sitesService/queries/sitesQueries";
import { SwRedux } from "app/services/SwRedux";
import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { NavigatorType } from "single-spa/react-app/ProNavigator";
import queryClient from "single-spa/react-app/query-client";

import DefaultDataFetcherFactory from "../widget-fetchers/DefaultDataFetcher";
import type { IDataFetcher, IDataFetcherFactory } from "../widget-fetchers/IDataFetcher";
import { getCampaignMaxAvailableDate, getRtCampaignDataStartDate } from "../widget-utilities/Campaigns/CampaignsUtils";
import { SEGMENTS_FAMILY_NAME } from "../widget-utilities/segments/constants";
import { WidgetCacheService } from "../widgetCacheService";

import SWWidget from "./widget.reg";

export enum WidgetState {
    EMPTY,
    LOADING,
    LOADED,
    ERROR,
    SOFT_LIMIT_REACHED,
    ASSET_NOT_ACCESSIBLE,
}

export interface SWItem {
    id?: string;
    appKey?: string;
    store?: string;
    category?: string;
    name: string;
    image: string;
    isVirtual?: boolean;
}

export type IWidgetModelTypesWebSource = "Desktop" | "Total" | "Mobile";
export type IWidgetModelTypesType =
    | "GeographyTable"
    | "BarChart"
    | "Graph"
    | "PieChart"
    | "SingleMetric"
    | "Table"
    | "ComparedBar"
    | "ComparedLine"
    | "IndustryKeywordsDashboardTable"
    | "IndustryReferralsDashboardTable"
    | "TopSitesTable"
    | "BarChartDemographics"
    | "BarChartMMXDashboard"
    | "PieChartMMXDashboard"
    | "BarChartDemographicsGender"
    | "AppEngagementMetricsDashboardGraph"
    | "AppRetentionDashboardGraph"
    | "AppRetentionDashboardTable"
    | "AppDemographicsGraph"
    | "KeywordAnalysisTotalVisits"
    | "KeywordsGraphDashboard"
    | "TableSearchKeywordsDashboard"
    | "TableWebsiteKeywordsDashboard"
    | "TableLandingPageDashboard"
    | "KeywordAnalysisGraphDashboard"
    | "KeywordAnalysisTrendOverTimeGraph"
    | "TableKeywordAnalysisDashboard"
    | "KeywordsDashboardTable"
    | "LeaderBySourceTable"
    | "TableDynamicColumnsLeader"
    | "RelatedCategoriesTable"
    | "TopReferralsDashboardTable"
    | "OutgoingReferralsDashboardTable"
    | "AudienceInterestsTable"
    | "PeriodOverPeriodVisits"
    | "AppEngagementOverviewTable"
    | "AppStoreInstallAndDownloadsOverviewTable"
    | "TrafficGrowthComparison"
    | "PaidSearchAdsTable"
    | "SearchAdsTable"
    | "SearchOverviewTotalVisits"
    | "SearchOrganicPaidOverviewPieChart"
    | "SearchOverviewTotalVisitsTable"
    | "SearchOrganicPaidOverviewTable"
    | "SegmentsMonthlyVisitsGraphWidget"
    | "SegmentsUniqueVisitorsGraphWidget"
    | "SegmentsPageViewsGraphWidget"
    | "SegmentsPagePerVisitGraphWidget"
    | "SegmentsVisitDurationGraphWidget"
    | "SegmentsBounceRateGraphWidget"
    | "SegmentsTrafficShareGraphWidget"
    | "SegmentsGroupMonthlyVisitsGraphWidget"
    | "SegmentsGroupTrafficShareGraphWidget"
    | "SegmentsPageViewsGroupGraphWidget"
    | "SegmentsPagePerVisitGroupGraphWidget"
    | "SegmentsVisitDurationGroupGraph"
    | "SegmentsBounceRateGroupGraphWidget"
    | "SegmentsUniqueVisitorsGroupGraph"
    | "MarketSharesWidget"
    | "MarketSharesGraphWidget"
    | "AppDistributionTableWidget"
    | "EngagedVisitsSupportGraphWidget"
    | "RankTrackerKeywordsReportTableWidget"
    | "RankTrackerOverviewPerformanceGraphWidget"
    | "AdIntelligencePerformanceOvertimeGraph"
    | "KeywordOverviewStats";

export type IWidgetModelTypesFamily =
    | "Website"
    | "Mobile"
    | "Industry"
    | "Segments"
    | "RankTracker"
    | "Keyword"
    | "Advertiser";

export interface IWidgetKeyBase {
    name: string; //App name | website url | keyword | category etc.
    type: IWidgetModelTypesFamily;
    image?: string;
    icon?: string;
    url?: string;
    id?: string;
}

export interface IWidgetWebsiteKey extends IWidgetKeyBase {
    isVirtual: boolean;
}

export interface IWidgetMobileKey extends IWidgetKeyBase {
    id: string; //app's store id
    store: string; //google|apple
    appKey: string; //[0|1]_{id}
}

export interface IWidgetKeywordsKey extends IWidgetKeyBase {
    keyword?: boolean;
}

export interface IWidgetIndustryKey extends IWidgetKeyBase {
    category?: boolean;
}

export interface IWidgetModelFilters {
    orderBy?: string;
    timeGranularity?: "Daily" | "Weekly" | "Monthly";
    filter?: string;
    segmentId?: string;
    segmentsGroupId?: string;
    IncludeBranded?: string;
    IncludeOrganic?: string;
    IncludePaid?: string;
    isEvergreen?: boolean;
    sites?: string;
    FuncFlag?: string;
    sourceType?: EKeywordTabs;
}

export interface IWidgetModel {
    dataMode?: string;
    country?: number | string; //999, 840
    duration?: string; //730d, 3m
    comparedDuration?: string | boolean;
    selectedChannel?: string;
    family?: IWidgetModelTypesFamily;
    includesSegments?: boolean;
    type?: IWidgetModelTypesType;
    id?: string;
    webSource?: IWidgetModelTypesWebSource;
    metric?: string;
    key?: Array<IWidgetWebsiteKey | IWidgetMobileKey | IWidgetIndustryKey | IWidgetKeywordsKey> | null;
    filters?: IWidgetModelFilters;
    ShouldGetVerifiedData?: boolean;
    width?: 1 | 2 | 3 | 4;
    customAsset?: boolean;
    device?: EDevice;
    navigationState?: string;
    currentCampaignConfiguration?: CampaignConfigForDashboard;
    customIndustryId?: string;
}

export interface ISubtitleTextItem {
    text: ReactNode;
    tooltipValue: ReactNode;
}

export interface IWidgetViewData {
    subtitleTextItem?: ISubtitleTextItem;
    title: string;
    tooltip: string;
    country: { id: string; text: string; selectedLocations?: ILocationObject[] };
    duration: string;
    store?: string;
    key: Array<SWItem>;
    customSubtitle?: any;
    height?: number;
    errorHeight?: number;
    loadingHeight?: number;
    proUrl?: any;
    family?: any;
    legendsColorSource?: any;
    legendContainerClass?: any;
    trackName?: any;
    titleIcon?: any;
    titleTemplate?: any;
    customSubtitleComponent?: any;
}

export interface IWidgetParams {
    metric?: string;
    keys?: string;
    country?: number;
    from?: string;
    to?: string;
    isWindow?: boolean;
    store?: string;
    orderBy?: string;
    includeSubDomains?: boolean;
    timeGranularity?: "Daily" | "Weekly" | "Monthly";
    page?: number;
    webSource?: string;
    pageSize?: string;
    includeBranded?: boolean;
    device?: string;
    appMode?: string;
    terms?: string;
    ShouldGetVerifiedData?: boolean;
    filter?: string;
    isUtm?: boolean;
    compareFrom?: boolean;
    IncludeUrls?: string;
    ExcludeUrls?: string;
    IncludeTerms?: string;
    ExcludeTerms?: string;
    serpFilterTypes?: string;
    tableTab?: string;
    granularity?: string;
    rangeFilter?: string;
    intentsFilter?: string;
    tags?: string;
    excludeTags?: string;
    withUntagged?: string;
    untaggedFilter?: UntaggedFilterValue;
    excludeKeywords?: string;
    includeKeywords?: string;
    invertedSerpFilter?: string;
    mainSiteSerpFilter?: string;
    mainSitePositionTrend?: string;
    asc?: string;
    useCaseFilter?: string;
    selectedSites?: string;
    sites?: string;
    duration?: string;
    multiIncludeExcludeKeywords?: string;
    multiIncludeExcludeUrl?: string;
    multiIncludeExcludeTitle?: string;
    pageFilter?: string;
    source?: string;
    adType?: string;
    key?: string;
    isDaily?: boolean;
    selectedCategory?: string;
    domains?: string;
}

export interface IWidgetViewOptions {
    cssClass?: string;
    loadingSize: number;
    showIndex?: boolean;
    showTitle: boolean;
    showTitleTooltip: boolean;
    showSubtitle: boolean;
    showLegend: boolean;
    legendAlign: string;
    showFrame: boolean;
    showSettings: boolean;
    showTopLine: boolean;
    titleType: string;
    subtitleMarginBottom?: string;
    hideMainSubtitle?: boolean;
    showMmxMobileAlert?: boolean;
    showCountry?: boolean;
    showWebSource?: boolean;
    desktopOnly?: boolean;
    dashboardSubtitleMarginBottom?: number;
    dashboardSubtitleClass?: string;
    hideDurationFromSubtitle?: boolean;
    isMobileOrDesktopOnly?: boolean;
    noLoading?: boolean;
    preserveLegendSpace?: boolean;
    showOverflow?: boolean;
    chartIdForAnnotations?: any;
    tableCompareLegend?: boolean;
    template?: string;
    useBulletLegends?: boolean;
    useNewLegends?: boolean;
    noBoxShadow?: boolean;
    noBottomPadding?: boolean;
    noTopPadding?: boolean;
    frameClass?: any;
    hook?: any;
    target?: any;
    ctaButtonTracking?: any;
    ctaButton?: any;
    link?: any;
    floatingLegend?: any;
    legendsGridColumnGap?: any;
    titlePaddingBottom?: any;
    titleClass?: any;
    inlineBorderless?: any;
    hasCountryLegend?: boolean;
}

export interface IWidget<T> extends IEmitterEmit, IEmitterListener {
    // properties
    id: string;
    widgetState: WidgetState;
    type: string;
    data: any;
    errorReason: object | null;
    metadata: any;
    utilityGroups: any;
    // getters/setters
    bottomUtilities?: any;
    includesSegments?: boolean;
    apiParams: IWidgetParams;
    viewData: IWidgetViewData;
    viewOptions: IWidgetViewOptions;
    templateUrl: string;
    templateComponent?: any; // NEW for moving to React
    periodOverPeriodLegend?: boolean;
    beingEdited?: boolean;
    hideAddToDashboard?: boolean;
    originalData?: any;
    legendClass?: any;
    isPptSupported?: () => boolean;
    getDataForPpt?: () => IPptSlideExportRequest;
    getSearchKey?: () => string;
    dashboardId?: string;
    isCompare?: any;

    subtitleData?: any;
    webSource?: any;
    subtitle?: any;

    widgetConfig?: any;
    durationObject?: any;
    chartConfig?: any;
    customLegendTemplate?: any;
    periodOverPeriodLegendItems?: any;
    isMobileWebCountry?: boolean;
    afterRender?: any;
    legendItems?: any;
    _widgetConfig?: any;
    _metricConfig?: any;
    pos?: any;
    container?: any;
    readOnly?: boolean;
    reactLegendComponent?: any;
    onSeriesToggle?: any;
    _params?: any;
    errorConfig?: any;
    titleUtility?: any;
    properties?: any;
    maxMonths?: any;
    // methods
    getClass(): any;
    getWidgetConfig(): any;
    getWidgetMetricConfig(): any;
    getDataFetcher(): IDataFetcher<T>;
    onWidgetMount(params: { $el: any; $scope: any }): void;
    getTitleTemplate();
    getData<T>(): void | Promise<void>;
    onNewDataReceived(response: any): void | Promise<void>;
    callbackOnGetData(response: any): void | Promise<void>;
    injectWidget(injections: Record<string, any>): void;
    //initModules(): void;
    initWidget(widgetConfig: any, context: string): void;
    initWidgetWithConfigs(config: any, context: string): void;
    runWidget(): void;
    getWidgetModel(): IWidgetModel;
    getWidgetFilters?(): IWidgetModelFilters;
    getExporter(): any;
    getProperties();
    canAddToDashboard(): boolean;
    hasShowMoreButton(): boolean;
    isDashboard(): boolean;
    handleUtilityAction(utility: any, value: any): void;
    findUtility(utilityName: string, group: number): any;
    updateApiParams(paramsToAdd: any): void;
    clearApiParams(paramsToDelete: any): void;
    mergeViewData?: (params: any) => IWidgetViewData;
}

export interface IWidgetConfig {
    type: string;
    properties: any;
}

const DEFAULT_WIDGET_HEIGHT = "200px";

export const getKeyForApi = function (key: SWItem[]) {
    if (key.length < 1) {
        return { keys: "" };
    }
    const store = key[0].store;
    const returnValue: any = {
        keys: key.map((item) => item.id || (item.isVirtual ? "*" + item.name : item.name)).join(","),
    };
    if (store) {
        returnValue.store = store.charAt(0).toUpperCase() + store.substr(1);
    }
    if (key[0].category) {
        returnValue.keys = key[0].category;
    }
    return returnValue;
};

export const emptyWidget = {
    id: null,
    pos: {},
    properties: {
        country: null,
        duration: null,
        comparedDuration: null,
        family: "Website",
        filters: {},
        key: [],
        metric: null,
        type: null,
    },
    segmentId: null,
    customIndustryId: null,
    segmentsGroupId: null,
    getProperties: function (clone = false) {
        return clone ? cloneDeep(this.properties) : this.properties;
    },
};

// Default widget view options are for custom dashboard widgets
const defaultViewOptions: IWidgetViewOptions = {
    cssClass: "swTable--simple",
    loadingSize: 5,
    showTitle: true,
    showTitleTooltip: false,
    showSubtitle: true,
    showLegend: true,
    legendAlign: "right",
    showFrame: true,
    showSettings: true,
    showTopLine: true,
    titleType: "link",
};

export enum WidgetMessages {
    WIDGET_GET_DATA_SUCCESS = "WidgetGetDataSuccess",
    WIDGET_GET_DATA_FAIL = "WidgetGetDataFail",
    WIDGET_LOADED = "WidgetLOADED",
    WIDGET_LODAING = "WidgetLOADING",
    WIDGET_CLEANUP = "widgetCleanup",
    WIDGET_UPDATED = "widgetUpdated",
    WIDGET_TAB_CHANGE = "widgetTabChange",
}

export abstract class Widget implements IWidget<any> {
    public _params: any = {};

    protected _viewOptions: any = {};

    protected _viewData: any = {};

    protected _utilitiesData: any = {};

    public _metricConfig: any;

    public _metricTypeConfig: any;

    public _widgetConfig: any;

    public includesSegments?: boolean;

    protected _widgetModel: IWidgetModel;

    protected _durationService = DurationService;

    protected _defaultFetcherFactory: IDataFetcherFactory<any>;

    protected _reduxService: SwRedux;

    protected _events: EventEmitter;

    private keywordGroupCache: Map<string, IUserKeywordGroup> = new Map();

    _swNavigator: NavigatorType;

    widgetConfig: any;

    private _widgetCacheService: WidgetCacheService;

    protected _i18nFilter: any;

    private _apiController: string;

    private _context: string;

    protected _autoFetchData = true;

    protected _$timeout: any;

    protected _dataFetcher: IDataFetcher<any>;

    id: string;

    dashboardId: string;

    widgetState: number = WidgetState.LOADING;

    type: string;

    data: any;

    originalData: any;

    metadata: any;

    utilityGroups: any;

    titleUtility: any;

    bottomUtilities: any;

    family: string;

    filters: any;

    pos: any;

    width: any;

    webSource: string;

    segmentId: string | null;

    customIndustryId: string | null;

    segmentsGroupId: string | null;

    legendItems: any;

    parsedCampaigns: any;

    durationObject: IDurationData;

    public errorConfig: any = {
        icon: "no-data",
        messageTop: "home.dashboards.widget.table.error1",
        messageBottom: "home.dashboards.widget.table.error2",
        wide: true,
    };

    errorReason: object | null;

    container: string;

    titleTemplates = {
        i18nKey: () => {
            return this._i18nFilter(this._widgetConfig.properties.title || this._metricConfig.title);
        },
        i18nKeyDurationPrefix: () => this.titleTemplates.i18nKey() + " (" + this._viewData.duration + ")",
        custom: () => this._widgetConfig.properties.title,
    };

    static register() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const clazz = this;
        const name = clazz.name;
        if (!name) {
            console.error(`Failed to register ${clazz}: missing name`);
        } else {
            SWWidget[name] = clazz;
        }
    }

    /*
     * Here _componentId is used to build the API endpoint url, in  the new method of '/api/componentId/metric/method'
     */
    constructor() {
        // initialize events
        this._events = new EventEmitter();
        this._swNavigator = proNavigator;
        this._widgetCacheService = WidgetCacheService.getInstance();
        this._i18nFilter = i18nFilter();
        // default to direct default fetcher (solid baseline for dashboard widgets)
        this._defaultFetcherFactory = DefaultDataFetcherFactory as unknown as IDataFetcherFactory<any>;
        this._reduxService = SwRedux.getInstance();
    }

    /**
     * Initializes the widget with injections.
     */
    public injectWidget(injections = {}) {
        Object.entries(injections).forEach(([injName, injInst]) => {
            this[`_${injName}`] = injInst;
        });
    }

    /**
     * Static methods
     */
    /**
     * @ngdoc Classes
     * @module shared
     * @class Widget
     * @method parseKeyString
     * @static
     * @description used to set widget key from page url param
     * @todo temporary, key should be set from chosenItems service and not from url
     */
    static getWidgetResponsiveWidth(width: number) {
        const DASHBOARD_COLS = 12,
            RESPONSIVE_RATIO = 1.5;
        width = width * RESPONSIVE_RATIO;
        return width > DASHBOARD_COLS ? DASHBOARD_COLS : width;
    }

    static filterParse(filterString): Record<string, { operator: string; value: string }> {
        const filterObject = {};
        if (typeof filterString === "string" && filterString !== "") {
            filterString.split(",").forEach((filterString) => {
                const filterArray = filterString.split(";");
                filterObject[filterArray[0]] = {
                    operator: filterArray[1],
                    value: filterArray[2],
                };
            });
        }
        return filterObject;
    }

    static getWidgetMetadataType() {
        return "Base";
    }

    static getWidgetResourceType() {
        return "Base";
    }

    static filterStringify(filterObject) {
        const filterArray = [];
        each(filterObject, (filter, key) => {
            filterArray.push(`${key};${filter.operator};${filter.value}`);
        });
        return filterArray.join(",");
    }

    protected static toBoolean(value) {
        if (isString(value)) {
            return value.toLowerCase() === "true";
        }
        if (isBoolean(value)) {
            return value;
        }
        return false;
    }

    /**
     * Private methods
     */
    private _setUtilities() {
        const utilityGroups = widgetSettings.getWidgetUtilities(this._widgetConfig.utilityGroups);
        const utilityGroupsPartition = partition(
            utilityGroups,
            (utilityGroup: any) => utilityGroup.properties.className === "titleRowLeft",
        );
        this.utilityGroups = utilityGroupsPartition[1];
        // for container tabs
        if (utilityGroupsPartition[0].length) {
            this.titleUtility = utilityGroupsPartition?.[0]?.[0]?.utilities?.[0];
        }

        // this code is for utilities below the widget-component section
        const groups = partition(
            this.utilityGroups,
            (utilityGroup: any) => utilityGroup.properties.className === "tableBottom",
        );
        if (groups[0].length) {
            this.bottomUtilities = groups[0];
        }
        this.utilityGroups = groups[1];
    }

    private _appendDurationToTitle() {
        const widgetProp = this._widgetConfig.properties;
        return widgetProp.title === "__ADD_DATE RANGE__" || widgetProp.appendDurationToTitle;
    }

    private _getDurationDataForInitialParams(
        widgetProp: typeof this._widgetConfig.properties,
        component: string | undefined,
    ): IDurationData {
        // RT dashboard durations ("all", custom ranges) need getDurationDataForCampaigns with campaign bounds.
        // dataStart is firstScrapeTime (not createdTime); pre-scrape campaigns fall back to max day.
        if (widgetProp.family === EFamilyTypes.RankTracker) {
            const config = widgetProp.currentCampaignConfiguration;
            const maxDate = getCampaignMaxAvailableDate(config?.lastScrapeDate);
            const dataStart = getRtCampaignDataStartDate(config) ?? dayjs.utc(maxDate).startOf("day");
            return this._durationService.getDurationDataForCampaigns(widgetProp.duration, dataStart, maxDate);
        }
        if (
            result(this._metricTypeConfig, "properties.periodOverPeriodSupport", false) ||
            result(this._widgetConfig, "properties.periodOverPeriodSupport", false)
        ) {
            return this._durationService.getDurationData(widgetProp.duration, widgetProp.comparedDuration, component);
        }
        return this._durationService.getDurationData(widgetProp.duration, null, component);
    }

    private _pickDurationApiParams(forAPI: IDurationData["forAPI"]) {
        const { iso, ...serializableParams } = forAPI;
        return serializableParams;
    }

    private _setInitialParams(widgetModelFilters) {
        const params: any = {};
        const widgetProp = this._widgetConfig.properties;
        const metricProp = this._metricConfig;
        const _isCompare = widgetProp.key.length > 1;
        const _metricFilters = widgetSettings.getMetricWidgetFilters(
            widgetProp.metric,
            widgetProp.type,
            _isCompare,
            true,
        );

        const _model = this._widgetConfig.properties;

        if (!isEmpty(_metricFilters)) {
            widgetProp.filters = Object.assign(_metricFilters || {}, widgetProp.filters || {});
        }
        widgetProp.duration = widgetProp.forcedDuration ? widgetProp.forcedDuration : widgetProp.duration;

        const widgetCustomApiParams = Object.assign(
            {},
            widgetSettings.getWidgetCustomApiParams(widgetProp, widgetProp.metric),
            widgetProp.apiParams,
            metricProp.apiParams,
            widgetModelFilters,
        );
        // use period over period only in supported metrics
        const component =
            this._metricConfig.componentFunction?.({
                key: this._widgetConfig?.properties?.key,
            }) ?? this._metricConfig.component;
        this.durationObject = this._getDurationDataForInitialParams(widgetProp, component);
        const durationApiParams = this._pickDurationApiParams(this.durationObject.forAPI);
        const widgetCache = this._getWidgetCache();
        const store = widgetProp.key.length > 0 && getKeyForApi(widgetProp.key).store;

        // the current url params were passed into the widget config and spread
        params.country = widgetProp.country;
        params.device = widgetProp.device;
        params.store = widgetProp.store;
        params.appMode = widgetProp.appMode || widgetProp.mode;
        params.terms = widgetProp.terms;
        params.metric = widgetSettings.getMetricId(widgetProp.metric);
        params.includeSubDomains = widgetProp.isWWW !== "-";
        params.webSource =
            _model.family === "Mobile"
                ? undefined
                : result(this._metricTypeConfig, "properties.options.desktopOnly", false)
                  ? "Desktop"
                  : widgetProp.webSource;

        if (widgetProp.categoryHash) {
            params.categoryHash = widgetProp.categoryHash;
        }

        if (_model.family === SEGMENTS_FAMILY_NAME) {
            params.segmentId = get(widgetProp, "segmentId", undefined) || get(widgetProp, "key[0].id", undefined);
        }

        if (_model.family === "Industry") {
            params.customIndustryId = getCustomIndustryIdFromModel(widgetProp.key);
        }

        if (widgetProp.customAsset) {
            const assetKey: any = _model.key;
            switch (widgetProp.customAsset) {
                case "Industry":
                    const customCategory = categoryService.getCategory(`*${assetKey[0].name}`);
                    if (customCategory) {
                        params.categoryHash = customCategory.categoryHash;
                    } else {
                        params.categoryHash = undefined;
                    }
                    break;
                case "Keyword":
                    /**
                     * @todo: THIS SHOULD BE REMOVED!!!
                     */
                    const keywordsGroupList: IUserKeywordGroup[] = [
                        ...queryClient.getQueryData<IUserKeywordGroup[]>(keywordGroupsKeys.user()),
                        ...queryClient.getQueryData<IUserKeywordGroup[]>(keywordGroupsKeys.shared()),
                    ];
                    const keywordsGroup = keywordsGroupList.find((group) => group.Name === assetKey[0].name);
                    if (keywordsGroup) {
                        params.GroupHash = keywordsGroup.GroupHash;
                    }
            }
        }

        if (widgetProp.category) {
            params.category = categoryService.categoryQueryParamToCategoryObject(widgetProp.category)?.forDisplayApi;
        }

        if (widgetProp.disableBrowserCache) {
            params.timestamp = Math.floor(Date.now());
        }

        //default for missing params
        if (widgetProp.filters && params.metric === "AppRanks") {
            widgetProp.filters.appmode = widgetProp.filters.appmode || "Top Free";
            widgetProp.filters.device = widgetProp.filters.device || (store === "Google" ? "AndroidPhone" : "iPhone");
        }

        if (widgetProp.family === EFamilyTypes.RankTracker) {
            params.campaignguid = _model.currentCampaignConfiguration?.id ?? _model.campaignguid;
            params.sites = widgetProp.key.map((key) => key.id).join(",");
            params.duration = widgetProp.duration;
        }

        if (widgetProp.duration === "28d" && widgetProp.family !== EFamilyTypes.RankTracker) {
            const module = this._swNavigator.current().name.substring(0, this._swNavigator.current().name.indexOf("."));
            params.timeGranularity = module === "industryAnalysis" ? "Monthly" : "Daily";
            if (widgetCache && widgetCache.timeGranularity) {
                widgetCache.timeGranularity = "Daily";
            }
        } else if (widgetProp.family !== EFamilyTypes.RankTracker) {
            params.timeGranularity = this._metricConfig.timeGranularity || "Monthly";
        }
        // backward compatibility for TimeGranularity param
        if (widgetProp.filters && widgetProp.filters.hasOwnProperty("TimeGranularity")) {
            widgetProp.filters.timeGranularity = widgetProp.filters.TimeGranularity;
            delete widgetProp.filters.TimeGranularity;
        }

        if (widgetProp.family === "Industry") {
            if (widgetProp.filters?.hasOwnProperty("ShouldGetVerifiedData")) {
                delete widgetProp.filters.ShouldGetVerifiedData;
            }
        }

        //This solution verifies keywords groups and custom categories API calls in custom dashboards widget
        //have the unique hash in order to force request when the custom asset content has been updated.
        if (widgetProp.key.length > 0 && _model.key[0].id) {
            if (
                this.dashboardId &&
                ["Keyword", "Industry"].indexOf(_model.family) > -1 &&
                _model.key[0].id.indexOf(_model.key[0].name) === -1 &&
                _model.key[0].id.indexOf("~") === -1
            ) {
                const customAssetId = _model.key[0].id;
                if (_model.family == ("Keyword" as IWidgetModelTypesFamily)) {
                    /**
                     * @todo: THIS SHOULD BE REMOVED!!!
                     */
                    const keywordGroupList: IUserKeywordGroup[] = [
                        ...queryClient.getQueryData<IUserKeywordGroup[]>(keywordGroupsKeys.user()),
                        ...queryClient.getQueryData<IUserKeywordGroup[]>(keywordGroupsKeys.shared()),
                    ];
                    const keywordGroup = keywordGroupList.find((group) => group.Id === customAssetId);
                    if (keywordGroup) {
                        params.GroupHash = keywordGroup.GroupHash;
                    }
                } else if (_model.family == ("Industry" as IWidgetModelTypesFamily)) {
                    const customCategory = UserCustomCategoryService.getCustomCategoryById(customAssetId);
                    if (customCategory) {
                        params.categoryHash = customCategory.categoryHash;
                    }
                }
            }
        }

        merge(
            this._params,
            params,
            durationApiParams,
            widgetProp.filters,
            getKeyForApi(this._widgetConfig.properties.ignoreCompareMode ? [head(widgetProp.key)] : widgetProp.key),
            widgetCustomApiParams,
            widgetCache,
        );
    }

    private _setFilterParam(filterParamString) {
        // deconstruct filterParamString to object
        const filterParamObject = Widget.filterParse(filterParamString);
        // deconstruct existing _params.filter to object
        const filterObject = Widget.filterParse(this._params.filter);
        merge(filterObject, filterParamObject);
        // check for filters with empty strings and delete them
        each(filterObject, (filter: any, key) => {
            if (filter.value === '""' || filter.value === "null" || filter.value === "") {
                delete filterObject[key];
            }
        });
        if (isEmpty(filterObject)) {
            delete this._params.filter;
        } else {
            this._params.filter = Widget.filterStringify(filterObject);
        }
        // on each filter always reset the page number
        delete this._params.page;
    }

    protected _removePrefix(value, keyPrefix) {
        if (value && value.length > 0 && value.indexOf(keyPrefix) == 0) {
            return value.substring(1, value.length);
        }
        return value;
    }

    public getProUrl(rowParams?) {
        let state;
        if (this.apiParams.webSource === "MobileWeb" && this._metricConfig.mobileState) {
            state = this._metricConfig.mobileState;
        } else {
            state = rowParams
                ? this._metricConfig.state || ""
                : this._metricConfig.titleState || this._metricConfig.state || "";
        }
        const result = Object.assign(this._getProUrlParams(), this._metricConfig.stateParams, rowParams);
        if (typeof state === "undefined") {
            return this._swNavigator.href("home", {}, {});
        }
        return this._swNavigator.href(state, result, {});
    }

    protected _getProUrlParams() {
        const { selectedAppDataSource } = this._reduxService.getState().appDataSource;
        const isApp = this._viewData.family === "Mobile";
        const isIndustry = this._metricConfig.family === "Industry";
        const isKeywordAnalysis = this._metricConfig.family === "Keyword";
        const keyObject = getKeyForApi(this._viewData.key);
        const result: any = {
            isWWW:
                this.apiParams.includeSubDomains === undefined ||
                this.apiParams.includeSubDomains === true ||
                this.apiParams.includeSubDomains === "true"
                    ? "*"
                    : "-",
            duration: this._widgetConfig.properties.duration,
            comparedDuration: this._widgetConfig.properties.comparedDuration,
            country: (this._viewData.country && this._viewData.country.id) || 999,
            webSource: this._widgetConfig.properties.webSource,
            ...this._metricConfig.customProQueryParams,
        };
        const _model: IWidgetModel = this.getWidgetModel();
        if (isApp) {
            result.appId = `${Number((keyObject.store ?? "").toLowerCase() !== "google")}_${keyObject.keys}`;
            if (selectedAppDataSource === undefined) {
                result.device = this._params.device;
            }
        } else if (isIndustry && _model.key[0]) {
            if (keyObject.keys.indexOf("$") > -1) {
                result.category = this._removePrefix(keyObject.keys, "$");
            } else {
                const categoryId = _model.key[0].id;
                const category = UserCustomCategoryService.getCustomAndSharedCategoryById(categoryId);
                result.category = category?.forUrl;
            }
        } else if (isKeywordAnalysis && _model.key[0]) {
            if (_model.key[0].id && _model.key[0].id != _model.key[0].name) {
                const group = this.getCachedKeywordGroup(_model.key[0].id);
                result.keyword = group ? `*${group.Id}` : `*${_model.key[0].id}`;
            } else {
                result.keyword = _model.key[0].name;
            }
        } else {
            result.key = keyObject.keys;
        }
        return result;
    }

    private getCachedKeywordGroup(keywordId: string): IUserKeywordGroup | undefined {
        return this.keywordGroupCache.get(keywordId);
    }

    protected async preloadKeywordGroups(): Promise<void> {
        const _model: IWidgetModel = this.getWidgetModel();
        const isKeywordAnalysis = this._metricConfig.family === "Keyword";

        if (isKeywordAnalysis && _model.key && _model.key[0]) {
            const keywordId = _model.key[0].id;
            if (keywordId && keywordId !== _model.key[0].name && !this.keywordGroupCache.has(keywordId)) {
                try {
                    const group = await keywordGroupsQueryClient.getGroupById(keywordId);
                    if (group) {
                        this.keywordGroupCache.set(keywordId, group);
                    }
                } catch (error) {
                    Sentry.captureException(error, {
                        extra: {
                            keywordId,
                            widgetId: this.id,
                        },
                    });
                }
            }
        }
    }

    protected getDuration() {
        // Init already built durationObject via _getDurationDataForInitialParams (campaign-aware).
        // Falling through to getDurationData would break on "all" (undefined raw → createTextRepresentation crash).
        if (this.durationObject?.forWidget != null) {
            return this.durationObject.forWidget;
        }
        const component =
            this._metricConfig.componentFunction?.({
                key: this._widgetConfig?.properties?.key,
            }) ?? this._metricConfig.component;
        return this._durationService.getDurationData(this._widgetConfig.properties.duration, null, component).forWidget;
    }

    protected _getExcelEndPoint() {
        const ctrl = this._apiController || this._widgetConfig._apiController;
        if (ctrl) {
            const excelMetric = this._widgetConfig.properties.excelMetric || this._widgetConfig.properties.metric;
            return `/widgetApi/${ctrl}/${excelMetric}/Excel?`;
        } else {
            return `/api/WidgetKpis/GetExcel?`;
        }
    }

    protected handleDataError(statusCode: number) {
        switch (statusCode) {
            case 400:
            case 404:
            case 500:
            default:
                break;
        }
        // remove bottom message on non-dashboard widgets
        if (!this.dashboardId) {
            delete this.errorConfig.messageBottom;
        }
    }

    private _getHash() {
        const context = this._context || "";
        return `${context}_${this._widgetConfig.properties.metric}_${this._widgetConfig.type}`;
    }

    protected _updateWidgetCache() {
        // cache only specified params
        const cachedParams = pick(this._params, this._widgetConfig.properties.cachedParams);
        if (!isEmpty(cachedParams)) {
            this._widgetCacheService.put(this._getHash(), cachedParams);
        }
    }

    protected mergeGAVerifiedFlag(response, dataRecords?) {
        const keys = this._params.keys.split(",");
        if (!response.KeysDataVerification) {
            return;
        }

        const _swConnectedAccountsService = connectedAccountsServiceFactory();
        const _mainKey = keys[0];

        //Add isGAVerified flag on the widget.data object if at least one key is verified.

        this.data = this.data || {};

        this.data.isGAVerified = false;
        for (let i = 0; i < keys.length; i++) {
            if (response.KeysDataVerification[keys[i]]) {
                this.data.isGAVerified = true;
                break;
            }
        }

        //Add isGAPrivate flag on the widget.data object only if the first key us Private.
        this.data.isGAPrivate = _swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(_mainKey);

        if (this.isCompare()) {
            //Compare mode - add isGAVerified flag for each of the widget.key objects and widget.legendItem objects.
            each(response.KeysDataVerification, (isGAVerified, domain) => {
                const _dataObj: any =
                    find(dataRecords, { Domain: domain }) ||
                    response.Data[domain] ||
                    find(response.Data, { Domain: domain });

                //Add isGAVerified falg to the widget's data object.
                if (_dataObj) {
                    _dataObj.isGAVerified = isGAVerified;
                    _dataObj.isGAPrivate =
                        _swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(domain);
                }

                //Add isGAVerified to the widget's legend object.
                const _legendObj: any = this.legendItems[domain] || find(this.legendItems, { name: domain });
                if (_legendObj) {
                    _legendObj.isGAVerified = isGAVerified;
                    _legendObj.isGAPrivate =
                        _swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(domain);
                    _legendObj.metric = this.apiParams.metric;
                }
            });
        }
    }

    public isCompare() {
        return this.getProperties().key.length > 1;
    }

    private _getWidgetCache() {
        return this._widgetCacheService.get(this._getHash());
    }

    /**
     * Getter/Setter methods
     */
    get apiParams() {
        return this._params;
    }

    // Object passed to this setter extends _params
    set apiParams(params: any) {
        const newParams = (this._params = Object.assign({}, this._params));
        for (const param in params) {
            if (param === "filter") {
                this._setFilterParam(params[param]);
            } else {
                newParams[param] = params[param];
            }
        }
        if (this._apiController) {
            this.initFetcherFromConfig();
        }
        this.notifyWidgetUpdated();
        this.getData();
    }

    public updateApiParams(paramsToAdd: any) {
        Object.assign(this._params, paramsToAdd);
    }

    public clearApiParams(paramsToDelete: any) {
        paramsToDelete && paramsToDelete.forEach((param) => delete this._params[param]);
    }

    get viewData() {
        return this._viewData.getterCompleted ? this._viewData : this.getViewData();
    }

    set viewData(params: any) {
        this.mergeViewData(params);
    }

    getViewData() {
        const widgetProp = this._widgetConfig.properties;
        const viewData: any = this._viewData;
        const viewOptions: any = this._viewOptions;
        viewData.customSubtitle = this._i18nFilter(widgetProp.subtitle || this._metricConfig.subtitle);
        viewData.key = widgetProp.key;
        viewData.family = this._metricConfig.family;
        viewData.country = CountryService.countriesById[viewOptions.overrideCountry || widgetProp.country];
        viewData.duration = viewData.duration || this.getDuration();
        viewData.forcedDuration = widgetProp.forcedDuration;
        viewData.title = viewData.title || this._getTitle();
        viewData.titleClass = viewData.titleClass || "";
        viewData.tooltip = viewData.tooltip || this._getTooltip();
        viewData.proUrl = viewData.proUrl || this.getProUrl();
        viewData.height = widgetProp.height || DEFAULT_WIDGET_HEIGHT;
        viewData.errorHeight = widgetProp.errorHeight || viewData.height;
        viewData.width = parseInt(widgetProp.width || widgetSettings.getDefaultWidgetSize(this.type)) || 4;
        viewData.widthSmallScreen = Widget.getWidgetResponsiveWidth(viewData.width);
        viewData.legendsColorSource = this._viewOptions.newColorPalette
            ? "compareMainColors"
            : this._viewOptions.audienceOverviewColors
              ? "audienceOverview"
              : null;
        viewData.legendContainerClass = this._viewOptions.legendContainerClass || "";
        viewData.titleIcon = this.getTitleIcon();
        // set loading height on widgets with height:'auto'
        viewData.loadingHeight = widgetProp.loadingHeight || widgetProp.height;
        viewData.getterCompleted = true;
        viewData.trackName = viewOptions.trackName = widgetProp.trackName;
        viewData.desktopOnly = this._viewOptions.desktopOnly;
        viewData.addToDashboardIconClass = this._viewOptions.addToDashboardIconClass || "";
        return viewData;
    }

    // Object passed to this setter extends _viewData
    mergeViewData(params: any) {
        return Object.assign(this._viewData, params);
    }

    get viewOptions() {
        return this._viewOptions;
    }

    get templateUrl() {
        const { widgetTemplateUrl = `/app/components/widget/widget-templates/${this.type.toLowerCase()}.html` } =
            this._widgetConfig.properties;
        return widgetTemplateUrl;
    }

    get templateComponent() {
        return null;
    }

    getLegendItems(data?: any) {
        return this._widgetConfig.properties.key;
    }

    public get excelUrl() {
        const newParams: any = merge({}, this._params);
        if (newParams.metric) {
            delete newParams.metric;
        }
        const excelParams = reduce(
            newParams,
            (paramString, paramVal: any, paramKey) => {
                if (paramVal !== undefined) {
                    return `${paramString}${paramKey}=${encodeURIComponent(paramVal)}&`;
                } else {
                    return paramString;
                }
            },
            "",
        );

        return this._getExcelEndPoint() + trimEnd(excelParams, "&");
    }

    /**
     * Protected Methods
     */
    protected initFetcherFromConfig() {
        //check if metric has support for custom fetcher and either widget is not in
        // dashboard or it is but the metric says to override the fetching factory
        const hasCustom =
            this._metricConfig?.customFetcherFactory &&
            (!this.dashboardId || this._metricConfig.customFetcherFactory?.overrideDashboardFetching);

        if (hasCustom) {
            const factoryName = this._metricConfig.customFetcherFactory.factoryName;
            const customFactory = (allDataFetchers as any)?.[factoryName] as IDataFetcherFactory<any> | undefined;

            if (customFactory) {
                this._dataFetcher = customFactory.create(this as any, {
                    statePath: this._metricConfig.customFetcherFactory.path,
                });
                return;
            }
        }

        const fetcherFactory: IDataFetcherFactory<any> =
            this._widgetConfig.widgetDataFetcher ||
            this._widgetConfig.properties.widgetDataFetcher ||
            this.getFetcherFactory();
        this._dataFetcher = fetcherFactory.create(this as any);
    }

    protected getFetcherFactory() {
        return this._defaultFetcherFactory;
    }

    public getDataFetcher() {
        return this._dataFetcher;
    }

    public getWidgetConfig() {
        return this._widgetConfig;
    }

    public getWidgetMetricConfig() {
        return this._metricConfig;
    }

    /**
     * Static methods
     */

    static decorateWidgetConfig(widgetConfig) {
        const widgetProps = widgetConfig.properties;
        return {
            ...widgetConfig,
            properties: {
                ...widgetProps,
                options: widgetProps.options || {},
                tooltip: widgetProps.tooltip
                    ? widgetProps.tooltip
                    : widgetProps.title
                      ? widgetProps.title + ".tooltip"
                      : "",
            },
        };
    }

    static decorateWidgetMetricConfig(widgetProps, metricProps, _widgetConfig, _metricTypeConfig) {
        // Override metric default properties used by multiple widgets // for example engagementVisits has different titles for different widgets in dashboards
        if (result(_metricTypeConfig, "properties.modules")) {
            return Object.assign(metricProps, _metricTypeConfig.properties.modules[widgetProps.family]);
        }
        return {
            ...metricProps,
            component: result(_widgetConfig, "properties.component", metricProps.component),
            tooltip: metricProps.title ? metricProps.title + ".tooltip" : "",
            includesSegments: _widgetConfig.includesSegments,
        };
    }

    /**
     * Public methods
     */

    public getClass(): any {
        return this.constructor;
    }

    initWidget(widgetConfig, context) {
        // edit references to support existing (wrong behavior) - will be removed when this function will be removed
        widgetConfig.properties.options = widgetConfig.properties.options || {};
        widgetConfig = Widget.decorateWidgetConfig(widgetConfig);
        const widgetProps = widgetConfig.properties;
        const widgetSettingsService = widgetSettings;
        const metricProps = widgetSettingsService.getMetricProperties(widgetProps.metric, widgetProps.family);
        const metricTypeConfig = widgetSettingsService.getMetricWidgetMetadata(
            widgetProps.metric,
            widgetProps.type,
            widgetProps.key.length > 1,
        );
        const metricConfig = Widget.decorateWidgetMetricConfig(
            widgetProps,
            metricProps,
            widgetConfig,
            metricTypeConfig,
        );
        const apiController = widgetConfig.properties.apiController || metricConfig.apiController;
        const viewOptions = merge(
            {},
            this._viewOptions,
            defaultViewOptions,
            widgetSettings.getWidgetOptions(widgetProps, widgetProps.metric),
        );

        this.initWidgetInternal(widgetConfig, metricConfig, metricTypeConfig, apiController, viewOptions, context);
    }

    initWidgetWithConfigs(config, context) {
        const widgetSettingsService = widgetSettings;
        config.metricConfig = {
            ...config.metricConfig,
            ...widgetSettingsService.getMetricProperties(config.metricConfig.metric, config.metricConfig.family),
        };
        this.initWidgetInternal(
            config.widgetConfig,
            config.metricConfig,
            config.metricTypeConfig,
            config.apiController,
            config.viewOptions,
            context,
        );
    }

    private initWidgetInternal(widgetConfig, metricConfig, metricTypeConfig, apiController, viewOptions, context) {
        const widgetProps = widgetConfig.properties;
        this._context = context;
        this.container = widgetProps.container;
        this.id = widgetConfig.id;
        this.dashboardId = widgetConfig.dashboardId;
        this.includesSegments = widgetConfig?.includesSegments;
        this.pos = widgetConfig.pos;
        this.width = widgetProps.width;
        this.type = widgetProps.type;
        this.webSource = widgetProps.webSource;
        this._metricTypeConfig = metricTypeConfig;
        this._widgetConfig = widgetConfig;
        this._metricConfig = metricConfig;
        this._apiController = apiController;
        if (widgetProps.clearWidgetCache) {
            this.clearWidgetCache();
        }
        this._setInitialParams(widgetProps.filters);
        this._setUtilities();
        this._viewOptions = viewOptions;
        this.initFetcherFromConfig();
        this.legendItems = this.getLegendItems();
        this._autoFetchData = widgetProps?.autoFetchData || this._autoFetchData;

        this.preloadKeywordGroups();
    }

    runWidget() {
        if (this._autoFetchData) {
            this.getData();
        }
        this.setMetadata();
        this.getLegendImage();
    }

    getLegendImage() {
        //Fallback if legendItems not defined!
        if (!this.legendItems) {
            return;
        }
        for (const legendItem of this.legendItems) {
            if (!this.getIcon(legendItem)) {
                if (typeof legendItem.id === "string") {
                    void fetchWebsiteImageQuery({ website: legendItem.id as string })
                        .then((data) => {
                            legendItem.image = data.image;
                        })
                        .catch(() => {});
                }
            }
        }
    }

    getIcon(item) {
        return item.icon || item.Icon || item.image;
    }

    async onNewDataReceived(response) {
        let dataValid;
        this.errorReason = null;
        try {
            dataValid = this.validateData(response?.Data);
        } catch (e) {
            console.error(`Data Validation failed: %O`, response?.Data, e);
        }
        if (!dataValid) {
            this.widgetState = WidgetState.ERROR;
            this.errorReason = response;
            this.emit(WidgetMessages.WIDGET_GET_DATA_SUCCESS, response);
            this.handleDataError(404);
            // console.warn(`Widget has empty data: %O`, response.Data);
            return;
        }

        this.widgetState = WidgetState.LOADED;

        this.originalData = response.Data;
        const result = this.callbackOnGetData((this._widgetConfig.processResponse || identity)(response));
        if (result instanceof Promise) {
            await result;
        }

        this.emit(WidgetMessages.WIDGET_GET_DATA_SUCCESS, response);
    }

    private async setErrorReason(reason) {
        if ("response" in reason && reason.response instanceof Response) {
            const { response, ...other } = reason;

            try {
                this.errorReason = {
                    ...other,
                    responseData: await response.clone().json(),
                };
            } catch {
                this.errorReason = other;
            }
        } else {
            this.errorReason = reason;
        }
    }

    async getData(): Promise<void> {
        if (this._metricConfig?.isDeprecated) {
            this.widgetState = WidgetState.LOADED;
            return;
        }
        this._updateWidgetCache();
        this.widgetState = WidgetState.LOADING;
        const requestParams = this._params;
        //added this emit because we need to know state changes always
        this.emit(WidgetMessages.WIDGET_LODAING);
        // fix #SIM-12998 - force async call in case the response is already cached.
        // this will make sure to kick start the ng-if of highchart directive in graph.html.
        // we must have this ng-if because ng-highcharts is extending our config object only when
        // the link function is invoked.
        // NOTE: force defer to fetch data in order to let finish the fetch settings setup (like apiParams).
        // try {
        //     const response = await this._dataFetcher.fetch();
        //     if (requestParams === this._params) {
        //         // response data might not reflect the latest API params!!!
        //         return this.onNewDataReceived(response);
        //     } else {
        //         // we can't be sure if this response is the latest and overiding a previous correct response
        //         // or just irrelevant any.
        //         // so to be 100% sure we will issue the request again with the latest params.
        //         return this.getData();
        //     }
        // } catch (reason) {
        //     this.widgetState = WidgetState.ERROR;
        //     this.emit(WidgetMessages.WIDGET_GET_DATA_FAIL, reason);
        //     this.handleDataError(reason.status);
        //
        // } finally {
        //     this.notifyWidgetUpdated();
        // }
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                this._dataFetcher
                    .fetch()
                    .then(async (response) => {
                        if (requestParams === this._params) {
                            // response data might not reflect the latest API params!!!
                            const result = this.onNewDataReceived(response);
                            return result instanceof Promise ? await result : result;
                        } else {
                            // we can't be sure if this response is the latest and overiding a previous correct response
                            // or just irrelevant any.
                            // so to be 100% sure we will issue the request again with the latest params.
                            return this.getData();
                        }
                    })
                    .catch(async (reason) => {
                        this.widgetState = WidgetState.ERROR;
                        await this.setErrorReason(reason);
                        this.emit(WidgetMessages.WIDGET_GET_DATA_FAIL, reason);
                        this.handleDataError(reason.status);
                    })
                    .finally(() => {
                        this.notifyWidgetUpdated();
                    })
                    .then(resolve);
            });
        });
    }

    public getProperties(clone = false) {
        return clone ? cloneDeep(this._widgetConfig.properties) : this._widgetConfig.properties;
    }

    /**
     * Since we had *****.toJson in the code before, I added this function which mimics it
     * the filter for $$ is to remove ***** "private" properties
     * This should be moved to just JSON.stringify() when move away from ***** is complete
     * @param source - object to stringify
     */
    toJson(source) {
        if (!source) {
            return null;
        }
        const newVal = {};
        Object.keys(source)
            .filter((key) => !key.startsWith("$$"))
            .map((key) => {
                newVal[key] = source[key];
            });

        return JSON.stringify(newVal);
    }

    getDBPresentation() {
        const customIndustryId =
            this?._widgetConfig?.customIndustryId || getCustomIndustryIdFromModel(this?._widgetConfig?.properties?.key);

        const isCustomCategory = !!UserCustomCategoryService.getCustomAndSharedCategoryById(customIndustryId);

        return {
            id: this.id,
            dashboardId: this.dashboardId,
            pos: this.toJson(this.pos),
            properties: this.toJson(this.getProperties()),
            segmentId: this?._widgetConfig?.segmentId || this?._widgetConfig?.properties?.key[0]?.segmentId,
            segmentsGroupId:
                this?._widgetConfig?.segmentsGroupId || this?._widgetConfig?.properties?.filters?.segmentsGroupId,
            ...(isCustomCategory && { customIndustryId }),
        };
    }

    setUtilityData(utilityId: string, utilityData: any) {
        this._utilitiesData[utilityId] = utilityData;
    }

    clearWidgetCache() {
        this._widgetConfig.properties.clearWidgetCache = false;
        this._widgetCacheService.remove(this._getHash());
    }

    protected _getTitle() {
        const titleTemplate = this._widgetConfig.properties.titleTemplate || "i18nKey";
        return this.titleTemplates[titleTemplate]();
    }

    private _getTooltip() {
        return this._i18nFilter(this._widgetConfig.properties.tooltip || this._metricConfig.tooltip);
    }

    public getExporter(): any {
        return WidgetExporter;
    }

    public onWidgetRemoval(): void {}

    /**
     * Abstract methods
     */
    abstract callbackOnGetData(response: any, comparedItemKeys?: any[]): void | Promise<void>;

    protected abstract setMetadata();

    protected abstract validateData(response: any): boolean;

    abstract onResize(): void;

    public handleUtilityAction(utility: any, value: string) {
        const utilityId = utility.id;
        const name = `${camelCase(utilityId)}UtilityAction`;
        const fn: Function = this[name] || noop;
        fn.apply(this, [utility, value]);
    }

    protected timeGranularityUtilityAction(utility, value) {
        const paramsObject = {} as any;
        paramsObject.timeGranularity = value;

        // update widget api params only if at least one param has really changed
        if (!isEqual(pick(this.apiParams, keys(paramsObject)), paramsObject)) {
            this.apiParams = paramsObject;
        }
    }

    public findUtility(utilityName: string, group: number) {
        return find(this.utilityGroups[group].utilities, (utility: any) => {
            return utility.type == utilityName;
        });
    }

    public getWidgetModel(): IWidgetModel {
        const _props = this.getProperties();
        this._widgetModel = {
            country: _props.country,
            duration: _props.duration,
            comparedDuration: _props.comparedDuration,
            family: _props.family,
            type: _props.type,
            metric: _props.metric,
            webSource: _props.webSource,
            includesSegments: this.includesSegments,
            key: this.reduceKeys(_props.key, this.apiParams.keys),
            ShouldGetVerifiedData: this.apiParams.ShouldGetVerifiedData,
            customAsset: _props.customAsset,
        };
        return this._widgetModel;
    }

    public getWidgetFilters(): IWidgetModelFilters {
        return {};
    }

    private reduceKeys(propsKeys: any, apiParamsKeys: any): any {
        if (apiParamsKeys == undefined || propsKeys.length === 1) {
            return propsKeys;
        }
        const keysArr = apiParamsKeys.split(",");
        if (propsKeys.length == keysArr.length) {
            return propsKeys;
        }
        return propsKeys.filter(function (key) {
            return includes(keysArr, key.name);
        });
    }

    /**
     * Shows a2d button if returned true
     * @returns {boolean}
     */
    public canAddToDashboard() {
        if (this._viewOptions.hasOwnProperty("canAddToDashboard")) {
            return this._viewOptions.canAddToDashboard;
        }
        if (!this._metricConfig.hasWebSource && this.webSource === "MobileWeb") {
            return false;
        }
        if (
            this.getWidgetModel().metric === "EngagementDesktopVsMobileVisits" &&
            this.getWidgetModel().webSource != "Total"
        ) {
            return false;
        }
        return this._metricConfig.dashboard == "true";
    }

    /**
     * Shows 'showMore' button if metric config property 'showMoreButtonItems' greater then number of data records.
     * @returns {boolean}
     */
    public hasShowMoreButton() {
        if (this._metricTypeConfig && this._metricTypeConfig.properties) {
            const metricTypeProps = this._metricTypeConfig.properties;
            if (metricTypeProps.showMoreButton) {
                return true;
            }
            if (this.data && this.data.Records && metricTypeProps.showMoreButtonItems) {
                return this.data.Records.length > metricTypeProps.showMoreButtonItems;
            }
        }
        return false;
    }

    public isDashboard() {
        return this._widgetConfig && this._widgetConfig.dashboardId && this._widgetConfig.dashboardId.length;
    }

    public emit(eventName: string, ...args: any[]) {
        this._events.emit(eventName, ...args);
    }

    public broadcast(eventName: string, ...args: any[]) {
        return this._events.emit(eventName, ...args);
    }

    public on(eventName: string, handler: (...args: any[]) => void) {
        return this._events.on(eventName, handler);
    }

    public off(eventName: string, handler: (...args: any[]) => void) {
        // @ts-ignore
        return this._events.off(eventName, handler);
    }

    public removeAllListeners(eventName: string) {
        return this._events.removeAllListeners(eventName);
    }

    protected notifyWidgetUpdated() {
        this._events.emit(WidgetMessages.WIDGET_UPDATED, this);
    }

    public onWidgetMount({ $el, $scope }) {
        // do nothing
    }

    public getTitleTemplate() {
        return this.viewOptions.titleTemplate || "/app/components/widget/containers/widget-title.html";
    }

    protected cleanup() {
        this.emit(WidgetMessages.WIDGET_CLEANUP);
        if (isFunction(this._dataFetcher.destroy)) {
            this._dataFetcher.destroy?.();
        }
    }

    /**
     * To define the parameter used to filter results with free text.
     * @returns {string}
     */
    public getSearchKey() {
        return "SearchTerm";
    }

    /**
     * Return title icon according to web source. Relevant for IA and WA only at the moment.
     * @returns {any}
     */
    public getTitleIcon() {
        const webSrcIconMap = { total: "combined", mobileweb: "mobile-web", desktop: "desktop" };

        if (this._viewData.family != "Mobile") {
            const webSource = this._params.webSource ? this._params.webSource.toLowerCase() : false;

            if (
                this.isDashboard() &&
                this._widgetConfig.properties.metric === "TopReferrals" &&
                this._params.webSource
            ) {
                return webSrcIconMap[webSource] || "desktop";
            } else if (this.isDashboard() && this._metricConfig.hasWebSource) {
                return webSrcIconMap[webSource] || "combined";
            }
        }
    }
}
